import { Sport, Team } from '../types';
import type { PracticeTheme } from './types';

/**
 * Request payload for generating a practice plan
 */
export interface PracticePlanRequest {
  team: Team;
  duration: number; // in minutes
  desiredDrillStepsPerDrill?: number; // preferred number of steps per drill (1-5)
  usePositionGroups?: boolean;
  focusAreas: string[];
  date: string;
  startTime: string;
  location: string;
  theme: PracticeTheme;
  ageLevel: string;
  experienceLevel: string;
}

/**
 * Response structure from the AI service
 */
export interface GeneratedPracticePlan {
  practice: {
    team_id: string;
    date: string;
    start_time: string;
    end_time: string;
    location: string;
    theme: string;
  };
  drills: Array<{
    name: string;
    duration: number;
    description: string;
    focus_area: string;
    intensity_level?: DrillStepIntensity;
    position_groups: Array<{
      group_name: string;
      steps: DetailedDrill[];
    }>;
  }>;
}

/**
 * Drill step structure for AI-generated drills
 */
export type DrillStepIntensity = 'Walk-through' | 'Technique focused' | 'Game speed';

export interface DrillStep {
  name: string;
  setup: string;
  execution: string;
  coachingPoints: string[];
  equipment: string[];
  video_url: string;
}

/**
 * Detailed drill structure for AI-generated drills
 */
export interface DetailedDrill {
  name: string;
  setup: string;
  execution: string;
  coachingPoints: string[];
  equipment?: string;
  video_url?: string;
  duration: number;
  order_index: number;
}

/**
 * Helper type for the position groups by sport
 */
export type PositionGroupMap = {
  [key in Sport]: string[];
};

/**
 * Practice settings structure
 */
export interface PracticeSettings {
  duration: number;
  theme: PracticeTheme;
  focusAreas: string[];
  team: Team;
  date: string;
  startTime: string;
  location: string;
  ageLevel: string;
  experienceLevel: string;
}

/**
 * Error types for AI service
 */
export enum AiServiceErrorType {
  API_ERROR = 'API_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  PARSING_ERROR = 'PARSING_ERROR'
}

/**
 * Custom error class for AI service
 */
export class AiServiceError extends Error {
  type: AiServiceErrorType;
  
  constructor(message: string, type: AiServiceErrorType = AiServiceErrorType.API_ERROR) {
    super(message);
    this.name = 'AiServiceError';
    this.type = type;
  }
}

/**
 * Structure for the JSON plan validation
 */
export interface ValidationResult {
  isValid: boolean;
  issues: ValidationIssue[];
  fixedPlan?: GeneratedPracticePlan;
}

/**
 * Structure for validation issues
 */
export interface ValidationIssue {
  type: 'error' | 'warning';
  message: string;
  location?: string;
  fix?: () => void;
}

/**
 * Sport-specific rules for drills
 */
export interface SportRules {
  requiredPositionGroups: string[];
  totalDuration: number;
  minDrillDuration: number;
  maxDrillDuration: number;
  requiredWaterBreaks: number;
  waterBreakDuration: number;
  positionFilterFn: (position: string, theme: PracticeTheme) => boolean;
}

export type FocusArea = 'Offense' | 'Defense' | 'Special Teams';